import { type CSSProperties, useEffect } from 'react';
import { MessageBlock, Link } from '@cfa/react-core';
import styled from 'styled-components';
import { useNotificationsStore } from '../../../stores/globalNotificationStore';
import type { NotificationValues } from '../../../interfaces/NotificationInterface';
import { themeSizes, themeColors } from '../../../styles/globalStyleVars';

const MessageBlockWrapper = styled.div<{ $anchorTop: boolean }>`
  justify-content: center;
  padding-bottom: ${themeSizes.smMd};
  position: absolute;
  left: 0;
  right: 0;
  pointer-events: none;
  ${({ $anchorTop }) => $anchorTop && 'top: 4px;'}
`;

export const ActionLink = styled.a<{ $severity: string }>`
  /* We are manually adding 12px for now until we upgrade to React-Core 1.4.1 (see ticket: SAW-9914) */
  margin-top: ${themeSizes.smMd};
  font-weight: 500;
  cursor: pointer;
  color: ${({ $severity }) =>
    ['error', 'warning'].includes($severity)
      ? themeColors.primaryRed
      : themeColors.secondaryBlue};

  :hover {
    text-decoration: underline;
  }
`;

type StyledMessageBlockProps = {
  severity: 'info' | 'success' | 'warning' | 'error';
  handleClose?: (messageId?: number) => void;
  onClose?: () => void;
  messageId?: number;
  boldTextContent?: string;
  regularTextContent?: string;
  contentReverse?: boolean;
  stacked?: boolean;
  linkHREF?: string;
  linkText?: string;
  actionText?: string;
  actionLinkOnClick?: () => void;
  blockStyle?: CSSProperties;
};

const StyledMessageBlock = ({
  severity,
  handleClose,
  onClose,
  messageId,
  boldTextContent,
  regularTextContent,
  contentReverse,
  stacked,
  linkHREF,
  linkText,
  actionText,
  actionLinkOnClick,
  blockStyle,
}: StyledMessageBlockProps) => (
  <MessageBlock.Root
    {...(handleClose &&
      onClose && {
        onDismiss: () => handleClose(messageId),
      })}
    severity={severity}
    style={blockStyle}
  >
    {stacked ? (
      <>
        {boldTextContent && (
          <MessageBlock.Title>{boldTextContent}</MessageBlock.Title>
        )}
        {regularTextContent}
      </>
    ) : (
      <span>
        {boldTextContent && !contentReverse && <b>{boldTextContent}&nbsp;</b>}
        {regularTextContent && <span>{regularTextContent}</span>}
        {boldTextContent && contentReverse && <b>&nbsp;{boldTextContent}</b>}
      </span>
    )}
    {linkHREF && linkText && (
      <Link href={linkHREF} target='_blank' inline rel='noreferrer'>
        {linkText}
      </Link>
    )}
    {actionText && actionLinkOnClick && (
      <ActionLink $severity={severity} onClick={actionLinkOnClick}>
        {actionText}
      </ActionLink>
    )}
  </MessageBlock.Root>
);

export const StatusNotification = ({
  messageId,
  severity,
  linkHREF,
  linkText,
  actionText,
  actionLinkOnClick,
  onClose,
  timeToDisplay,
  regularTextContent,
  boldTextContent,
  contentReverse,
  stacked,
  hasWrapper = false,
  customWidth,
  anchorTop = false,
}: NotificationValues) => {
  const { removeNotification } = useNotificationsStore();

  const handleClose = (messageId?: number) => {
    if (messageId) removeNotification(messageId);
    onClose?.();
  };

  //biome-ignore lint/correctness/useExhaustiveDependencies: maintain min dependencies
  useEffect(() => {
    if (!timeToDisplay) return;
    const autoHideNotification = setTimeout(
      () => handleClose(messageId),
      timeToDisplay
    );
    return () => clearTimeout(autoHideNotification);
  }, [timeToDisplay]);

  const blockStyle = hasWrapper
    ? {
        minWidth: customWidth || '0px',
        width: 'fit-content',
        // Takes back the clicks the wrapper gives up, so a dismiss control still works.
        pointerEvents: 'auto' as const,
      }
    : { minWidth: '100%', whiteSpace: 'pre-line' };

  const messageBlock = (
    <StyledMessageBlock
      severity={severity}
      handleClose={handleClose}
      onClose={onClose}
      messageId={messageId}
      boldTextContent={boldTextContent}
      regularTextContent={regularTextContent}
      contentReverse={contentReverse}
      stacked={stacked}
      linkHREF={linkHREF}
      linkText={linkText}
      actionText={actionText}
      actionLinkOnClick={actionLinkOnClick}
      blockStyle={blockStyle}
    />
  );

  return hasWrapper ? (
    <MessageBlockWrapper className='row' $anchorTop={anchorTop}>
      {messageBlock}
    </MessageBlockWrapper>
  ) : (
    messageBlock
  );
};
